import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, Heart, Bell, Plus, Trash2, Eye, Edit, Play,
  Building2, BedDouble, MapPin, PoundSterling
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function SavedSearchesPage() {
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('searches');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [runningSearchId, setRunningSearchId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    search_criteria: {
      search_text: '',
      property_type: [],
      min_bedrooms: '',
      max_bedrooms: '',
      min_rent: '',
      max_rent: ''
    },
    email_alerts: false,
    push_alerts: false,
    alert_frequency: 'daily'
  });

  useEffect(() => {
    fetchSavedSearches();
    fetchSavedProperties();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      const response = await api.get('/prs-services/saved-searches');
      setSavedSearches(response.data.saved_searches);
    } catch (error) {
      toast.error('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedProperties = async () => {
    try {
      const response = await api.get('/prs-services/saved-properties');
      setSavedProperties(response.data.saved_properties);
    } catch (error) {
      toast.error('Failed to load saved properties');
    }
  };

  const handleCreateSearch = async (e) => {
    e.preventDefault();
    
    const criteria = { ...formData.search_criteria };
    // Clean up empty values
    Object.keys(criteria).forEach(key => {
      if (criteria[key] === '' || (Array.isArray(criteria[key]) && criteria[key].length === 0)) {
        delete criteria[key];
      }
    });
    
    try {
      await api.post('/prs-services/saved-searches', {
        name: formData.name,
        description: formData.description,
        search_criteria: criteria,
        email_alerts: formData.email_alerts,
        push_alerts: formData.push_alerts,
        alert_frequency: formData.alert_frequency
      });
      toast.success('Search saved successfully');
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        search_criteria: { search_text: '', property_type: [], min_bedrooms: '', max_bedrooms: '', min_rent: '', max_rent: '' },
        email_alerts: false,
        push_alerts: false,
        alert_frequency: 'daily'
      });
      fetchSavedSearches();
    } catch (error) {
      toast.error('Failed to save search');
    }
  };

  const handleRunSearch = async (searchId) => {
    setRunningSearchId(searchId);
    setSearchResults([]);
    try {
      const response = await api.post(`/prs-services/saved-searches/${searchId}/run`);
      setSearchResults(response.data.properties || []);
      toast.success(`Found ${response.data.count} properties`);
    } catch (error) {
      toast.error('Failed to run search');
    } finally {
      setRunningSearchId(null);
    }
  };

  const handleDeleteSearch = async (searchId) => {
    if (!window.confirm('Delete this saved search?')) return;
    
    try {
      await api.delete(`/prs-services/saved-searches/${searchId}`);
      toast.success('Search deleted');
      fetchSavedSearches();
      if (searchResults.length > 0) setSearchResults([]);
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  const handleUnsaveProperty = async (propertyId) => {
    try {
      await api.delete(`/prs-services/saved-properties/${propertyId}`);
      toast.success('Property removed from saved list');
      fetchSavedProperties();
    } catch (error) {
      toast.error('Failed to remove property');
    }
  };

  const togglePropertyType = (type) => {
    const types = formData.search_criteria.property_type || [];
    const newTypes = types.includes(type) 
      ? types.filter(t => t !== type)
      : [...types, type];
    setFormData({
      ...formData,
      search_criteria: { ...formData.search_criteria, property_type: newTypes }
    });
  };

  const PropertyCard = ({ property }) => (
    <div className="p-4 border rounded-lg hover:bg-slate-50">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium">{property.title || `${property.bedrooms} Bed Property`}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3" />
            {property.address_line_1}, {property.city}
          </p>
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className="flex items-center gap-1">
              <BedDouble className="h-4 w-4 text-slate-400" />
              {property.bedrooms} bed
            </span>
            <span className="flex items-center gap-1">
              <PoundSterling className="h-4 w-4 text-slate-400" />
              {property.rent_amount}/month
            </span>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => handleUnsaveProperty(property.id || property.property_id)}>
          <Heart className="h-4 w-4 fill-red-500 text-red-500" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="saved-searches-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Saved Searches & Properties</h1>
            <p className="text-slate-600">Manage your property searches and favorites</p>
          </div>
          <Button onClick={() => setShowCreateForm(true)} data-testid="create-search-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Search
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button 
            variant={activeTab === 'searches' ? 'default' : 'outline'}
            onClick={() => setActiveTab('searches')}
          >
            <Search className="h-4 w-4 mr-2" />
            Saved Searches ({savedSearches.length})
          </Button>
          <Button 
            variant={activeTab === 'properties' ? 'default' : 'outline'}
            onClick={() => setActiveTab('properties')}
          >
            <Heart className="h-4 w-4 mr-2" />
            Saved Properties ({savedProperties.length})
          </Button>
        </div>

        {showCreateForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create New Saved Search</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateSearch} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    placeholder="Search Name (e.g., London 2-bed flats)"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Search Criteria</label>
                  <Input
                    placeholder="Location or keyword..."
                    value={formData.search_criteria.search_text}
                    onChange={(e) => setFormData({
                      ...formData,
                      search_criteria: { ...formData.search_criteria, search_text: e.target.value }
                    })}
                    className="mb-3"
                  />

                  <div className="flex flex-wrap gap-2 mb-3">
                    {['flat', 'house', 'studio', 'room', 'bungalow'].map((type) => (
                      <Badge
                        key={type}
                        className={`cursor-pointer ${
                          (formData.search_criteria.property_type || []).includes(type)
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                        onClick={() => togglePropertyType(type)}
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <Input
                      type="number"
                      placeholder="Min beds"
                      value={formData.search_criteria.min_bedrooms}
                      onChange={(e) => setFormData({
                        ...formData,
                        search_criteria: { ...formData.search_criteria, min_bedrooms: e.target.value }
                      })}
                    />
                    <Input
                      type="number"
                      placeholder="Max beds"
                      value={formData.search_criteria.max_bedrooms}
                      onChange={(e) => setFormData({
                        ...formData,
                        search_criteria: { ...formData.search_criteria, max_bedrooms: e.target.value }
                      })}
                    />
                    <Input
                      type="number"
                      placeholder="Min rent £"
                      value={formData.search_criteria.min_rent}
                      onChange={(e) => setFormData({
                        ...formData,
                        search_criteria: { ...formData.search_criteria, min_rent: e.target.value }
                      })}
                    />
                    <Input
                      type="number"
                      placeholder="Max rent £"
                      value={formData.search_criteria.max_rent}
                      onChange={(e) => setFormData({
                        ...formData,
                        search_criteria: { ...formData.search_criteria, max_rent: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.email_alerts}
                      onChange={(e) => setFormData({...formData, email_alerts: e.target.checked})}
                    />
                    <Bell className="h-4 w-4" />
                    Email alerts
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.push_alerts}
                      onChange={(e) => setFormData({...formData, push_alerts: e.target.checked})}
                    />
                    Push notifications
                  </label>
                  <select
                    className="border rounded-md p-2"
                    value={formData.alert_frequency}
                    onChange={(e) => setFormData({...formData, alert_frequency: e.target.value})}
                  >
                    <option value="instant">Instant</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button type="submit">Save Search</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === 'searches' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Your Saved Searches
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : savedSearches.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No saved searches yet. Create one to get started.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedSearches.map((search) => (
                      <div
                        key={search.id}
                        className="p-4 border rounded-lg hover:bg-slate-50"
                        data-testid={`search-${search.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{search.name}</h3>
                            {search.description && (
                              <p className="text-sm text-slate-500">{search.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">{search.matching_count} matches</Badge>
                              {search.email_alerts && (
                                <Badge className="bg-blue-100 text-blue-800">
                                  <Bell className="h-3 w-3 mr-1" />
                                  Alerts
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1 mt-2">
                          {search.search_criteria.property_type?.map((type) => (
                            <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                          ))}
                          {search.search_criteria.min_bedrooms && (
                            <Badge variant="secondary" className="text-xs">{search.search_criteria.min_bedrooms}+ beds</Badge>
                          )}
                          {search.search_criteria.max_rent && (
                            <Badge variant="secondary" className="text-xs">≤£{search.search_criteria.max_rent}</Badge>
                          )}
                        </div>

                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            onClick={() => handleRunSearch(search.id)}
                            disabled={runningSearchId === search.id}
                          >
                            <Play className="h-3 w-3 mr-1" />
                            {runningSearchId === search.id ? 'Running...' : 'Run'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteSearch(search.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Search Results</CardTitle>
              </CardHeader>
              <CardContent>
                {searchResults.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Run a saved search to see results
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {searchResults.map((property) => (
                      <PropertyCard key={property.id} property={property} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'properties' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5" />
                Saved Properties
              </CardTitle>
            </CardHeader>
            <CardContent>
              {savedProperties.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No saved properties yet. Browse properties and click the heart icon to save.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedProperties.map((saved) => (
                    <div key={saved.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">Property ID: {saved.property_id}</p>
                          {saved.notes && (
                            <p className="text-sm text-slate-500 mt-1">{saved.notes}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            Saved: {new Date(saved.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => handleUnsaveProperty(saved.property_id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
